/**
 * The homepage hero's "spine field" — a row of vertical gold bars that breathe, and brighten
 * near the cursor. This is the one part of the homepage with no native section equivalent,
 * which is why the hero ships a component of its own.
 *
 * Each instance sizes to its canvas element rather than the viewport, and parks its animation
 * frame while scrolled out of view so an idle homepage costs nothing.
 */

const MH_HERO_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * One vertical bar of the field.
 * @typedef {{x: number, h: number, phase: number, speed: number, base: number}} MhSpineBar
 */

class MonarchSpineField {
  /** @param {HTMLCanvasElement} canvas */
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
    /** @type {MhSpineBar[]} */
    this.bars = [];
    this.mouseX = -9999;
    this.time = 0;
    /** @type {number | null} */
    this.raf = null;
    this.width = 0;
    this.height = 0;

    this.start = this.start.bind(this);
    this.draw = this.draw.bind(this);

    this.observe();
    this.bind();
    this.start();
  }

  build() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    if (!this.width || !this.height) return false;

    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const gap = this.width < 640 ? 12 : 16;
    const count = Math.ceil(this.width / gap) + 2;
    /** @type {MhSpineBar[]} */
    const bars = [];
    for (let i = 0; i < count; i++) {
      bars.push({
        x: i * gap + gap / 2,
        // Seeded off the index so a resize redraws the same skyline instead of reshuffling it.
        h: 0.35 + 0.6 * Math.abs((Math.sin(i * 12.9898) * 43758.5453) % 1),
        phase: (i * 1.7) % (Math.PI * 2),
        speed: 0.4 + ((i * 7) % 5) / 10,
        base: 0.05 + 0.09 * Math.abs(Math.cos(i * 4.1)),
      });
    }
    this.bars = bars;
    return true;
  }

  /**
   * @param {MhSpineBar} bar
   * @param {number} lum
   * @param {number} w
   */
  paint(bar, lum, w) {
    const barH = this.height * bar.h;
    const top = this.height * 0.5 - barH / 2;
    const grad = this.ctx.createLinearGradient(0, top, 0, top + barH);
    grad.addColorStop(0, 'rgba(201,162,75,0)');
    grad.addColorStop(0.5, `rgba(233,206,132,${lum})`);
    grad.addColorStop(1, 'rgba(201,162,75,0)');
    this.ctx.fillStyle = grad;
    this.ctx.fillRect(bar.x - w / 2, top, w, barH);
  }

  drawStatic() {
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.bars.forEach((bar) => this.paint(bar, bar.base + 0.05, 1.4));
  }

  draw() {
    this.time += 0.006;
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.bars.forEach((bar) => {
      let lum = bar.base + 0.06 * (0.5 + 0.5 * Math.sin(this.time * bar.speed + bar.phase));
      const dist = Math.abs(bar.x - this.mouseX);
      if (dist < 160) lum += (1 - dist / 160) * 0.5;
      this.paint(bar, Math.min(lum, 0.85), dist < 160 ? 2.4 : 1.4);
    });
    this.raf = requestAnimationFrame(this.draw);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  start() {
    this.stop();
    if (!this.build()) return;
    if (MH_HERO_MOTION.matches) {
      this.drawStatic();
      return;
    }
    this.draw();
  }

  observe() {
    new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.start();
        else this.stop();
      }
    }).observe(this.canvas);
  }

  bind() {
    /** @type {number | undefined} */
    let timer;
    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(this.start, 150);
      },
      { passive: true }
    );

    const stage = this.canvas.parentElement;
    if (stage) {
      stage.addEventListener(
        'pointermove',
        (event) => {
          const rect = this.canvas.getBoundingClientRect();
          this.mouseX = /** @type {PointerEvent} */ (event).clientX - rect.left;
        },
        { passive: true }
      );
      stage.addEventListener(
        'pointerleave',
        () => {
          this.mouseX = -9999;
        },
        { passive: true }
      );
    }

    MH_HERO_MOTION.addEventListener('change', this.start);
  }
}

for (const canvas of document.querySelectorAll('[data-mh-spinefield]')) {
  if (canvas instanceof HTMLCanvasElement) new MonarchSpineField(canvas);
}
